import { create } from 'zustand'
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { Resource, CreateResourceInput, UpdateResourceInput } from '../models/Resource'

// Helper to extract YouTube video ID from URL
function extractYoutubeId(url: string): string | undefined {
  const match = url.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
  )
  return match?.[1]
}

interface ResourceState {
  resources: Resource[]
  loading: boolean
  error: string | null
  fetchResources: () => Promise<void>
  addResource: (input: CreateResourceInput) => Promise<void>
  updateResource: (id: string, input: UpdateResourceInput) => Promise<void>
  deleteResource: (id: string) => Promise<void>
}

export const useResourceStore = create<ResourceState>((set, get) => ({
  resources: [],
  loading: false,
  error: null,

  fetchResources: async () => {
    set({ loading: true, error: null })
    try {
      const q = query(collection(db, 'resources'), orderBy('order', 'asc'))
      const snapshot = await getDocs(q)

      const resources: Resource[] = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          title: data.title,
          type: data.type,
          url: data.url,
          description: data.description,
          tags: data.tags || [],
          youtubeId: data.youtubeId,
          imageUrl: data.imageUrl,
          order: data.order || 0,
          createdAt: (data.createdAt as Timestamp).toDate(),
          updatedAt: data.updatedAt
            ? (data.updatedAt as Timestamp).toDate()
            : undefined,
          isActive: data.isActive ?? true,
        }
      })

      set({ resources, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入資源失敗',
      })
    }
  },

  addResource: async (input: CreateResourceInput) => {
    set({ loading: true, error: null })
    try {
      const youtubeId =
        input.type === 'youtube' ? extractYoutubeId(input.url) : undefined

      await addDoc(collection(db, 'resources'), {
        ...input,
        youtubeId,
        order: input.order ?? get().resources.length,
        createdAt: serverTimestamp(),
        isActive: true,
      })

      await get().fetchResources()
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '新增資源失敗',
      })
      throw error
    }
  },

  updateResource: async (id: string, input: UpdateResourceInput) => {
    set({ loading: true, error: null })
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateData: Record<string, any> = {
        ...input,
        updatedAt: serverTimestamp(),
      }

      if (input.url && input.type === 'youtube') {
        updateData.youtubeId = extractYoutubeId(input.url)
      }

      await updateDoc(doc(db, 'resources', id), updateData)
      await get().fetchResources()
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '更新資源失敗',
      })
      throw error
    }
  },

  deleteResource: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await deleteDoc(doc(db, 'resources', id))
      await get().fetchResources()
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '刪除資源失敗',
      })
      throw error
    }
  },
}))
